import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useCallback } from "react"
import { startQuest, QuestionAnswer } from "@/lib/api/quests"
import { useQuestsStore } from "@/lib/store/quests"
import { queryKeys } from "@/lib/utils/query"
import axios from "axios"

interface StartJourneyResult {
  success: boolean
  data?: any
  error?: any
  statusCode?: number
  questId: string
}

export const useStartJourney = (circleId: string) => {
  const { setStartingJourney } = useQuestsStore()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({
      questId,
      answers,
    }: {
      questId: string
      answers?: QuestionAnswer[]
    }): Promise<StartJourneyResult> => {
      setStartingJourney(true)
      try {
        const response = await startQuest(circleId, questId, answers)
        return {
          success: true,
          data: response,
          questId,
        }
      } catch (error) {
        // Extraemos el código de estado si es un error de Axios
        let statusCode
        if (axios.isAxiosError(error) && error.response) {
          statusCode = error.response.status
        }

        return {
          success: false,
          error,
          statusCode,
          questId,
        }
      }
    },
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: queryKeys.journeys })
      } else {
        console.error(
          "Error in journey start result:",
          result.error,
          "Status code:",
          result.statusCode,
        )
      }
    },
    onError: (error) => {
      console.error("Error starting journey:", error)
    },
    onSettled: () => {
      setStartingJourney(false)
    },
  })

  const startJourney = useCallback(
    async (
      questId: string,
      answers?: QuestionAnswer[],
    ): Promise<StartJourneyResult> => {
      const result = await mutation.mutateAsync({ questId, answers })
      return result
    },
    [mutation.mutateAsync],
  )

  return {
    startJourney,
    isStarting: mutation.isPending,
    error: mutation.error,
  }
}
